import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useAnimation,
  useInView,
} from "framer-motion";

const HowItWorks = forwardRef(({ inViewRef }, scrollRef) => {
  const ref = useRef(null);
  const controls = useAnimation();
  const isInView = useInView(ref, {
    margin: "0px 0px -150px 0px",
    once: true,
  });

  const [showMiddle, setShowMiddle] = useState(false);
  const [revealed, setRevealed] = useState(-1);
  const [showCircle, setShowCircle] = useState(false);

  const benefits = useMemo(() => {
    return [
      {
        id: 0,
        content: (
          <div className="flex items-center gap-2">
            <div className="w-fit bg-[#091F36] p-2">
              <img
                src="https://img.learntube.ai/public-assets/New%20Landing%20Page/thumbsup.svg?updatedAt=1744185899171"
                width={21}
                height={21}
                alt=""
                className="mx-auto"
              />
              <div className="mt-2 text-[10px] leading-[130%]">
                <p>Gain</p>
                <p>Recognition</p>
              </div>
            </div>
            <img
              src="https://img.learntube.ai/public-assets/New%20Landing%20Page/down-arrow.svg?updatedAt=1744184923146"
              width={12}
              height={14}
              className="rotate-90"
              alt=""
            />
          </div>
        ),
      },
      {
        id: 1,
        content: (
          <div className="flex flex-col items-center gap-2">
            <img
              src="https://img.learntube.ai/public-assets/New%20Landing%20Page/down-arrow.svg?updatedAt=1744184923146"
              width={12}
              height={14}
              className="-mr-2 rotate-[30deg]"
              alt=""
            />
            <div className="w-fit bg-[#091F36] p-2">
              <img
                src="https://img.learntube.ai/public-assets/New%20Landing%20Page/job-chance.svg?updatedAt=1744186634910"
                width={18}
                height={21}
                alt=""
                className="mx-auto"
              />
              <div className="mt-2 text-[10px] leading-[130%]">
                <p>
                  <span className="font-bold text-white">3x</span> your Job
                </p>
                <p>Chances</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 2,
        content: (
          <div className="flex flex-col items-center gap-2">
            <img
              src="https://img.learntube.ai/public-assets/New%20Landing%20Page/down-arrow.svg?updatedAt=1744184923146"
              width={12}
              height={14}
              className="-ml-4 -rotate-[30deg]"
              alt=""
            />
            <div className="w-fit bg-[#091F36] p-2">
              <img
                src="https://img.learntube.ai/public-assets/New%20Landing%20Page/Group_3.svg?updatedAt=1744186835820"
                width={21}
                height={21}
                alt=""
                className="mx-auto"
              />
              <div className="mt-2 text-[10px] leading-[130%]">
                <p>Get Noticed by</p>
                <p>Employers</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 3,
        content: (
          <div className="flex items-center gap-2">
            <img
              src="https://img.learntube.ai/public-assets/New%20Landing%20Page/down-arrow.svg?updatedAt=1744184923146"
              width={12}
              height={14}
              className="-rotate-90"
              alt=""
            />
            <div className={`w-fit bg-[#091F36] p-2`}>
              <img
                src="https://img.learntube.ai/public-assets/New%20Landing%20Page/increase-salary.svg?updatedAt=1744179161219"
                width={16}
                height={21}
                alt=""
                className="mx-auto"
              />
              <div className="mt-2 text-[10px] leading-[130%]">
                <p>Increase Salary</p>
                <p className="font-bold text-white">up to 150%</p>
              </div>
            </div>
          </div>
        ),
      },
    ];
  }, []);

  useEffect(() => {
    if (isInView) {
      setShowMiddle(true);
      const revealSteps = async () => {
        for (let i = 0; i <= benefits.length; i++) {
          await new Promise((res) => setTimeout(res, 600));
          setRevealed(i);
        }
        await new Promise((res) => setTimeout(res, 600));
        setShowCircle(true);
      };
      revealSteps();
    }
  }, [benefits.length, isInView]);

  useEffect(() => {
    const sequence = async () => {
      await controls.start({
        opacity: 1,
        scale: 1,
        transition: { duration: 1 },
      });
      controls.start({
        rotate: -360,
        transition: {
          repeat: Infinity,
          duration: 12,
          ease: "linear",
        },
      });
    };

    sequence();
  }, [controls, showCircle]);

  useEffect(() => {
    if (scrollRef && typeof scrollRef === "object") {
      scrollRef.current = ref.current;
    }
  }, [scrollRef]);

  return (
    <div
      ref={ref}
      className="text-xxs -ml-4 w-[calc(100%+2rem)] pb-[80px] text-[#83BEFF]"
    >
      <div ref={inViewRef} className="relative mt-4 text-center">
        <AnimatePresence>
          {showMiddle && (
            <>
              <motion.div
                className="relative flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative z-20 flex h-[92px] w-[92px] flex-col items-center justify-center rounded-full border-[4px] border-[#006FBC]/20 bg-[#0E4771] px-2 text-[10px]">
                  <img
                    src="https://img.learntube.ai/public-assets/New%20Landing%20Page/Quiz%201.svg?updatedAt=1744179014975"
                    width={21}
                    height={21}
                    alt=""
                  />
                  <p>Pass a Quick</p>
                  <p>Test</p>
                </div>
                <img
                  src="https://img.learntube.ai/public-assets/New%20Landing%20Page/down-arrow.svg?updatedAt=1744184923146"
                  width={12}
                  height={14}
                  className="relative z-20 -mt-1"
                  alt=""
                />
              </motion.div>
              <motion.div
                className="relative mx-auto flex w-fit flex-col items-center justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative z-20 flex h-[110px] w-[110px] flex-col items-center justify-center rounded-full border-[6px] border-[#006FBC]/20 bg-[#08578F] px-1 text-[10px]">
                  <img
                    src="https://img.learntube.ai/public-assets/New%20Landing%20Page/Certificate%201.svg?updatedAt=1744181188447"
                    width={22}
                    height={21}
                    alt=""
                  />
                  <p className="mt-2">Unlock Instant</p>
                  <p>Certificate</p>
                </div>
                {showCircle && (
                  <div className="absolute left-1/2 z-0 h-[203px] w-[203px] -translate-x-1/2 ">
                    <motion.img
                      src="https://img.learntube.ai/public-assets/New%20Landing%20Page/Group%2022973.png?updatedAt=1744727963291"
                      className="h-full w-full"
                      alt=""
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={controls}
                    />
                  </div>
                )}
                {showCircle && (
                  <div className="absolute  left-1/2 z-0 h-[243px] w-[243px] -translate-x-1/2">
                    <motion.img
                      src="https://img.learntube.ai/public-assets/New%20Landing%20Page/bigger-circle.png?updatedAt=1744727963586"
                      className="h-full w-full"
                      alt=""
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={controls}
                    />
                  </div>
                )}
                {showCircle && (
                  <div className="absolute left-1/2 z-10 h-[243px] w-[243px] -translate-x-1/2 rounded-full" />
                )}
                {benefits?.map((benefit, index) => {
                  const positions = [
                    "-left-[94px] top-5",
                    "-left-[30px] top-[95px]",
                    "-right-[38px] top-[95px]",
                    "-right-[110px] top-5",
                  ];
                  return (
                    <AnimatePresence key={index}>
                      {revealed >= benefit.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`absolute z-20 ${positions[index]}`}
                        >
                          {benefit?.content}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  );
                })}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

export default HowItWorks;
